import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { useQueryClient } from '@tanstack/react-query';
import { Megaphone, Settings, Users } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PermissionSlug } from '../../constants/featureFlags';
import { chatKeys } from '../../hooks/chat/useChatQueries';
import { MOTION } from './chatMotion';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useStore } from '../../store';
import { askBeforeLeaving } from '../../hooks/useLeaveGuard';

const MANAGE_GROUPS_HELP =
  'Create and edit shared groups. Clinic and discipline groups update on their own.';
const SHOW_ALL_HELP =
  'Also list announcement channels for clinics you are not in, so you can post there. Saved on this browser only.';

/**
 * "Chat settings" menu: Manage Groups for people who can edit groups, and the
 * "Show all announcements" switch for announcers. Shared by the dock and the
 * phone sheet inbox; `onNavigate` closes the host surface after the route
 * changes.
 */
export function ManageGroupsMenu({
  size,
  onNavigate,
}: {
  size: 'icon' | 'iconLg';
  onNavigate: () => void;
}) {
  const canManageGroups = useStore(state =>
    state.hasPermission(PermissionSlug.CHAT_EDIT_GROUPS)
  );
  const canAnnounce = useStore(state =>
    state.hasPermission(PermissionSlug.CHAT_SEND_ANNOUNCEMENT)
  );
  const showAll = useStore(state => state.chatShowAllAnnouncements);
  const setShowAll = useStore(state => state.setChatShowAllAnnouncements);
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  // A phone has no room to the left of the menu, so the help opens below.
  const helpSide = useIsMobile() ? 'bottom' : 'left';
  if (!canManageGroups && !canAnnounce) return null;
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          size={size}
          variant='ghost'
          aria-label='Chat settings'
          title='Chat settings'
        >
          <Settings className='h-3.5 w-3.5' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className={MOTION.reduceRadix}>
        <TooltipProvider delayDuration={300}>
          {canManageGroups && (
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuItem
                  onSelect={() => {
                    const go = () => {
                      navigate('/group-manager', {
                        state: { from: location.pathname + location.search },
                      });
                      onNavigate();
                    };
                    if (!askBeforeLeaving(go)) go();
                  }}
                >
                  <Users className='mr-2 h-4 w-4' />
                  Manage Groups
                </DropdownMenuItem>
              </TooltipTrigger>
              <TooltipContent
                side={helpSide}
                className={`max-w-[calc(100vw-2rem)] md:max-w-none ${MOTION.reduceRadix}`}
              >
                {MANAGE_GROUPS_HELP}
              </TooltipContent>
            </Tooltip>
          )}
          {canAnnounce && (
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuItem
                  role='menuitemcheckbox'
                  aria-checked={showAll}
                  onSelect={event => {
                    // Keep the menu open so the switch visibly flips.
                    event.preventDefault();
                    void (async () => {
                      // A first load still in flight read the old switch value, and
                      // invalidate alone would join it. Cancel it so the refetch
                      // starts with the new value.
                      await queryClient.cancelQueries({
                        queryKey: chatKeys.inbox(),
                      });
                      setShowAll(!showAll);
                      await queryClient.invalidateQueries({
                        queryKey: chatKeys.inbox(),
                      });
                    })();
                  }}
                >
                  <Megaphone className='mr-2 h-4 w-4' />
                  <span className='flex-1'>Show all announcements</span>
                  <span
                    aria-hidden='true'
                    className={`ml-3 inline-flex h-4 w-7 shrink-0 items-center rounded-full p-0.5 transition-colors ${
                      showAll ? 'bg-primary' : 'bg-input'
                    }`}
                  >
                    <span
                      className={`h-3 w-3 rounded-full bg-background transition-transform ${
                        showAll ? 'translate-x-3' : 'translate-x-0'
                      }`}
                    />
                  </span>
                </DropdownMenuItem>
              </TooltipTrigger>
              <TooltipContent
                side={helpSide}
                className={`max-w-[calc(100vw-2rem)] md:max-w-none ${MOTION.reduceRadix}`}
              >
                {SHOW_ALL_HELP}
              </TooltipContent>
            </Tooltip>
          )}
        </TooltipProvider>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
